import { useEffect, useRef, useState } from "react";
import { resolveMediaUrl } from "../../../../utility/media";
import { BACKEND_URL } from "../../../../services/Api";
import { IoClose, IoTrashOutline } from "react-icons/io5";
import { getActiveMembers, getAllActiveCommitteesForStaff } from "../../../../services/AppService";
import { useTranslation } from "react-i18next";

const AddStaffCommitteeForm = ({ onSubmit, editData, onClose, open, error }) => {
    const {t} = useTranslation();
    const [selectedCommittee, setSelectedCommittee] = useState("");
    const [active, setActive] = useState(true);
    
    // Each row has a unique 'key' for React rendering and the 'memberId'
    const [memberRows, setMemberRows] = useState([{ id: Date.now(), memberId: "" }]);

    const [committees, setCommittees] = useState([]);
    const [allMembers, setAllMembers] = useState([]);

  // Load Dropdowns once
    useEffect(() => {
        if (open) {
            getAllActiveCommitteesForStaff().then(res => setCommittees(res.data || res));
            getActiveMembers().then(res => setAllMembers(res.data || res));
        }
    }, [open]);

   // Handle Edit vs Add Mode
    useEffect(() => {
        if (open) {
            if (editData) {
                // EDIT MODE: Populate existing data
                setSelectedCommittee(editData.committeeId);
                setActive(editData.active);
                // Map existing member list to the row format
                const existingRows = editData.members.map(m => ({
                    id: m.id, // Using the assignment ID as row key
                    memberId: m.memberId || m.id // Adjust based on your response DTO
                }));
                setMemberRows(existingRows.length > 0 ? existingRows : [{ id: Date.now(), memberId: "" }]);
            } else {
                // ADD MODE: Reset to empty
                setSelectedCommittee("");
                setActive(true);
                setMemberRows([{ id: Date.now(), memberId: "" }]);
            }
        }
    }, [editData, open]);

    const addMemberRow = () => setMemberRows([...memberRows, { id: Date.now(), memberId: "" }]);
    
    const removeMemberRow = (id) => {
        if (memberRows.length > 1) {
            setMemberRows(memberRows.filter(row => row.id !== id));
        }
    };

    const handleMemberSelectChange = (id, value) => {
        setMemberRows(memberRows.map(row => row.id === id ? { ...row, memberId: value } : row));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Convert selections to numbers and filter empty ones
        const memberIds = memberRows
            .map(row => Number(row.memberId))
            .filter(id => id > 0);

        if (memberIds.length === 0) {
            alert("Please select at least one member.");
            return;
        }

        // Create the plain JSON object payload
        const payload = {
            committeeId: Number(selectedCommittee),
            membersId: memberIds, // Matches List<Long> membersId in Backend DTO
            active: true
        };

        // ✅ IMPORTANT: Pass the plain object, not FormData
        onSubmit(payload);
    };

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ maxWidth: '700px', width: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">{editData ? t('pageTitles.editCommittee') : t('pageTitles.addCommittee')}</div>
                    <button onClick={onClose}><IoClose /></button>
                </div>
                <div className="staff-form-body p-6">
                    <form onSubmit={handleSubmit}>
                        <div className="flex items-center gap-4 mb-6">
                            <label className="w-40">{t('forms.committee.committee')} <span className="text-red-500">(*)</span></label>
                            <select 
                                required 
                                value={selectedCommittee} 
                                onChange={(e) => setSelectedCommittee(e.target.value)} 
                                className="staff-modal-input flex-1"
                            >
                                <option value="">Select Committee</option>
                                {committees.map(c => <option key={c.id} value={c.id}>{c.committeeName || c.name}</option>)}
                            </select>
                        </div>

                        <button type="button" onClick={addMemberRow} className="bg-blue-600 text-white px-4 py-1 rounded text-sm mb-2">
                            {t('forms.committee.addMember')}
                        </button>

                        <div className="border rounded">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="p-2 border-r">{t('forms.committee.selectMember')}</th>
                                        <th className="p-2 w-20">{t('common.delete')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {memberRows.map((row) => (
                                        <tr key={row.id} className="border-b last:border-0">
                                            <td className="p-2 border-r">
                                                <select
                                                    required
                                                    value={row.memberId}
                                                    onChange={(e) => handleMemberSelectChange(row.id, e.target.value)}
                                                    className="w-full p-1 outline-none"
                                                >
                                                    <option className="text-center" value="">Select Member</option>
                                                    {allMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                                </select>
                                            </td>
                                            <td className="p-2 text-center">
                                                <button type="button" onClick={() => removeMemberRow(row.id)} className="text-gray-500 hover:text-red-600">
                                                    <IoTrashOutline size={20} color="red"/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-center mt-8">
                            <button type="submit" className="bg-blue-600 text-white px-10 py-2 rounded-md font-medium">{t('common.save')}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddStaffCommitteeForm;